import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DocumentPlusIcon, PhotoIcon, CubeIcon } from '@heroicons/react/24/outline';
import StaffShell from '../StaffShell';
import staffApi, { apiError } from '../api';
import { WORK_LABELS } from '../constants';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';
import { SkeletonRows } from '../components/Skeleton';

export default function MyReports() {
  const location = useLocation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(!!location.state?.justSubmitted);

  useEffect(() => {
    staffApi.get('/reports/')
      .then((r) => setReports(r.data.results || r.data))
      .catch((err) => setError(apiError(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 3000);
    return () => clearTimeout(t);
  }, [showToast]);

  const newBtn = (
    <Link to="/staff/app/report/new"
      className="inline-flex items-center gap-1.5 rounded-full bg-lime text-navy font-bold text-sm px-4 py-2 hover:bg-green-400 transition">
      <DocumentPlusIcon className="h-4 w-4" /> New report
    </Link>
  );

  return (
    <StaffShell backTo="/staff/app" max="max-w-2xl">
      {showToast && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-5 rounded-lg bg-lime/15 border border-lime/40 text-lime text-sm px-4 py-3">
          Report submitted. Nice work! 🎉
        </motion.div>
      )}

      <div className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-white text-2xl sm:text-3xl font-extrabold">My reports</h1>
        {newBtn}
      </div>

      {error && <p className="text-red-300 text-sm mb-4">{error}</p>}

      {loading ? (
        <SkeletonRows rows={5} />
      ) : reports.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState emoji="✍️" title="No reports yet"
            message="File your first job report to see it here." action={newBtn} />
        </div>
      ) : (
        <ul className="space-y-3">
          {reports.map((r, i) => (
            <motion.li key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              <Link to={`/staff/app/report/${r.id}`}
                className="block rounded-2xl border border-line bg-surface p-4 sm:p-5 hover:border-lime/60 hover:shadow-xl transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-ink font-bold truncate">{r.customer_name || WORK_LABELS[r.work_type] || 'Job'}</div>
                    <div className="text-ink-soft text-sm">{r.date} · {WORK_LABELS[r.work_type] || r.work_type}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="mt-3 flex items-center gap-4 text-ink-soft text-xs">
                  <span className="inline-flex items-center gap-1"><CubeIcon className="h-4 w-4" /> {(r.materials_used || []).length}</span>
                  <span className="inline-flex items-center gap-1"><PhotoIcon className="h-4 w-4" /> {(r.photos || []).length}</span>
                  {r.reviewed_by_username && <span className="text-lime font-semibold">Reviewed</span>}
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      )}
    </StaffShell>
  );
}
